import { Download, FileSpreadsheet, Loader2, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useCreateOrderReport, useOrderReports } from "../hooks/useReports";
import { OrderReport, ReportService, ReportStatus } from "../services/reportService";
import useToastStore from "../store/useToastStore";
import { formatDateTime } from "../utils/date";

const statusOptions = ["ALL", "PENDING", "CONFIRMED", "SHIPPED", "DELIVERED", "CANCELLED"];

const ReportsPage = () => {
  const { showToast } = useToastStore();
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    status: "ALL",
  });
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);

  const { data: reports = [], isLoading, isFetching, refetch } = useOrderReports();
  const createReport = useCreateOrderReport();

  const hasActiveReport = useMemo(
    () => reports.some((report) => report.status === "PENDING" || report.status === "PROCESSING"),
    [reports]
  );

  const handleGenerateReport = async () => {
    if (filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo) {
      showToast("From date cannot be after To date", "error");
      return;
    }

    try {
      await createReport.mutateAsync({
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        status: filters.status,
      });
      showToast("Report generation started", "success");
    } catch (error: any) {
      showToast(error.message || "Failed to create report", "error");
    }
  };

  const handleDownload = async (report: OrderReport) => {
    try {
      setDownloadingReportId(report.id);
      await ReportService.downloadReport(report);
    } catch (error: any) {
      showToast(error.message || "Failed to download report", "error");
    } finally {
      setDownloadingReportId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
            <p className="mt-1 text-sm text-gray-600">Generate XLSX order item reports with shipping details.</p>
          </div>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <section className="border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">From date</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">To date</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Order status</label>
              <select
                value={filters.status}
                onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status === "ALL" ? "All statuses" : status}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleGenerateReport}
                disabled={createReport.isPending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {createReport.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                Generate XLSX
              </button>
            </div>
          </div>
        </section>

        <section className="overflow-hidden border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Report History</h2>
              {hasActiveReport && <p className="text-xs text-gray-500">Active reports refresh every few seconds.</p>}
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center gap-2 px-4 py-16 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading reports
            </div>
          ) : reports.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-gray-500">No reports generated yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Created</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Filters</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Rows</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {reports.map((report) => (
                    <tr key={report.id}>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-900">{formatDateTime(report.createdAt)}</td>
                      <td className="px-4 py-3 text-gray-600">
                        <div>{report.filters.status === "ALL" ? "All statuses" : report.filters.status}</div>
                        <div className="text-xs text-gray-500">
                          {report.filters.dateFrom || "Start"} to {report.filters.dateTo || "Today"}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={report.status} />
                        {report.errorMessage && <div className="mt-1 max-w-xs text-xs text-red-600">{report.errorMessage}</div>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-600">{report.rowCount || "-"}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button
                          onClick={() => handleDownload(report)}
                          disabled={report.status !== "COMPLETED" || downloadingReportId === report.id}
                          className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {downloadingReportId === report.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }: { status: ReportStatus }) => {
  const styles: Record<ReportStatus, string> = {
    PENDING: "bg-yellow-50 text-yellow-700 border-yellow-200",
    PROCESSING: "bg-blue-50 text-blue-700 border-blue-200",
    COMPLETED: "bg-green-50 text-green-700 border-green-200",
    FAILED: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
};

export default ReportsPage;
